import { useEffect, useMemo, useRef, useState } from "react";
import { Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useW } from "./ctx";
import { docLabel } from "./lib";

/* One box for the whole project: doors, line items, clauses, sets, files, codes, cut sheets, and (supplier only) the catalog.
   "/" focuses it; arrows move; Enter jumps. */
export default function Search() {
  const w = useW();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const [catalog, setCatalog] = useState(null);
  const box = useRef(null);
  const input = useRef(null);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "/" && !/input|textarea/i.test(document.activeElement?.tagName)) { e.preventDefault(); input.current?.focus(); }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  useEffect(() => {
    if (open && w.role === "supplier" && !catalog) api.get("/condoor/catalog").then((r) => setCatalog(r.data.items)).catch(() => setCatalog([]));
  }, [open, w.role, catalog]);

  const index = useMemo(() => {
    const d = w.data;
    if (!d) return [];
    const out = [];
    d.doors.forEach((x) => out.push({ kind: "Door", key: x.door, label: `${x.door} · ${x.room}`, meta: `${x.set} · ${x.plan_ref || ""}`,
      go: () => w.openLine(d.lines.find((l) => l.door === x.door)?.id, "quick") }));
    d.lines.forEach((l) => out.push({ kind: "Line", key: l.id, label: `${l.id} · ${l.item}`, meta: l.cat, text: l.product, go: () => w.openLine(l.id, "quick") }));
    d.spec.clauses.forEach((c) => out.push({ kind: "Clause", key: c.id, label: `${c.id} · ${c.title}`, meta: `Spec p. ${c.page}`, go: () => w.openSource(c.id) }));
    d.spec.sets.forEach((s) => out.push({ kind: "Set", key: s.set, label: `${s.set} · ${s.application}`, meta: `${s.openings.length} openings`, go: () => w.openSource(s.set) }));
    ["submittal", "spec", "schedule", ...(d.interfaces ? ["interfaces"] : [])].forEach((k) =>
      out.push({ kind: "File", key: k, label: docLabel(k, d), go: () => w.openDoc(k) }));
    d.codes.forEach((c) => out.push({ kind: "Code", key: c.id, label: c.title.split(" · ")[0], meta: c.edition.split(" · ")[0], go: () => w.openDoc(`code:${c.id}`, "doc") }));
    Object.values(d.cut_sheets).forEach((c) => out.push({ kind: "Cut sheet", key: c.key, label: c.file, meta: c.mfr, go: () => w.openDoc(`cut:${c.key}`, "doc") }));
    (catalog || []).forEach((i) => out.push({ kind: "Catalog", key: i.code, label: `${i.code} · ${i.item}`, meta: i.mfr, go: () => w.openDoc("catalog") }));
    return out;
  }, [w, catalog]);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    const score = (r) => (r.key.toLowerCase() === t ? 0 : r.key.toLowerCase().startsWith(t) ? 1 : r.label.toLowerCase().includes(t) ? 2 : 3);
    return index.filter((r) => `${r.key} ${r.label} ${r.meta || ""} ${r.text || ""}`.toLowerCase().includes(t))
      .sort((a, b) => score(a) - score(b)).slice(0, 12);
  }, [q, index]);

  const go = (r) => { if (!r) return; r.go(); setOpen(false); setQ(""); input.current?.blur(); };

  return (
    <div ref={box} className="relative w-[340px] max-w-[40vw]" onBlur={(e) => !box.current?.contains(e.relatedTarget) && setOpen(false)}>
      <label className="flex h-8 items-center gap-2 rounded-full border border-[var(--line2)] bg-white px-3 text-[12.5px] focus-within:border-[var(--accent)]">
        <SearchIcon size={13} className="text-[var(--muted)]" />
        <input ref={input} value={q} placeholder="Search doors, lines, clauses, files" className="min-w-0 flex-1 bg-transparent outline-none" data-testid="search-input"
          onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setSel(0); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
            if (e.key === "Enter") go(results[sel]);
            if (e.key === "Escape") { setOpen(false); input.current?.blur(); }
          }} />
        <kbd className="mono rounded border border-[var(--line2)] px-1 text-[10px] text-[var(--muted)]">/</kbd>
      </label>
      {open && q.trim() && (
        <div className="absolute right-0 top-9 z-50 w-[420px] max-w-[80vw] overflow-hidden rounded-xl border border-[var(--line2)] bg-white shadow-xl" data-testid="search-results">
          {results.length === 0 ? <p className="px-3 py-3 text-[12.5px] text-[var(--muted)]">Nothing matches “{q}”.</p> : results.map((r, i) => (
            <button key={`${r.kind}-${r.key}`} tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => go(r)} onMouseEnter={() => setSel(i)}
              className={`flex w-full items-center gap-3 px-3 py-2 text-left text-[12.5px] ${i === sel ? "bg-[var(--accent-bg)]" : ""}`}>
              <span className="w-[64px] shrink-0 text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--muted)]">{r.kind}</span>
              <span className="min-w-0 flex-1 truncate">{r.label}</span>
              {r.meta && <span className="mono shrink-0 text-[11px] text-[var(--muted)]">{r.meta}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
